/* eslint-disable @typescript-eslint/no-explicit-any */
import { Description } from '@/types/watchface';
import { clearAll, uint32 } from '../utils';
import { ThemeData } from './compiler';
import {
  CONDITION,
  FILE,
  IMG,
  IMG_ARRAY,
  LAYOUT,
  SLOT,
  TRANSLATION,
  WIDGET
} from './dataSource';
import { genDataBinary } from './genDataBinary';
import { genHeaderBinary } from './genHeaderBinary';
import { genThemeAndRecordBinary } from './genThemeAndRecord';
import {
  genDataItemArcProgressBarData,
  genDataItemImageNumberData,
  genDataItemImageValuesData,
  genDataItemLineProgressBarData,
  genDataItemPointerData,
  genDataItemTextData,
  genImageArrayData,
  genImageData,
  genJSData,
  genJSImageData,
  genSlotData,
  genSpriteData,
  genTranslationData,
  genWidgetData,
  getConditionData,
  getRelateRecordUid,
  getAnimationData,
  genMultiSpriteData
} from './resToRecordData';
import {
  genM62DataBinary,
  genM62HeaderBinary,
  genM62ThemeAndRecordBinary
} from './compileM62';
import m65m66XmlConvert from './compileM65M66';
import { getThemeTableTotalCount } from './constant';

const HEADER_LENGTH = 168; // header长168个字节，v6.0.3增加64个字节  增加了recolorOrColorGroupTable 4
const LAYOUT_DATA_PRE_LENGTH = 16;
export let WATCHFACE_COLOR_GROUP: string[] = [];

let Multilingual = false; //是否开启多语言

export const themeData: ThemeData = {};
const themePrams: ThemeData = {
  data: {
    name: '',
    recordsCount: new Array(getThemeTableTotalCount()).fill(0),
    records: [],
    bg: '',
    bgColor: '',
    colorGroupTable: '',
    type: ''
  }
};

async function xmlConvert(
  ast: Ast,
  basicInfo: Description,
  resourcePath: string,
  is16bpp = false,
  protocol?: number
): Promise<Buffer[]> {
  // 初始化一下themeparams的recordsCount
  themePrams.data.recordsCount = new Array(getThemeTableTotalCount()).fill(0);

  const languages = new Map<string, any>();
  let isAOD = false;
  let isPhotoAlbumWatchface = false;
  let isVideoAlbumWatchface = false;
  // 二次导出重置数据
  Multilingual = false;
  WATCHFACE_COLOR_GROUP = [];
  Object.keys(themeData).forEach((key) => {
    delete themeData[key];
  });
  const { deviceType } = basicInfo;
  if (deviceType === 'M65S' || deviceType === 'M66') {
    return [await m65m66XmlConvert(ast, basicInfo, resourcePath)];
  }
  const isN65Plus = ['N65A', 'N65B', 'N65C'].includes(deviceType || '');
  const isN65C = ['N65C'].includes(deviceType || '');

  const {
    name,
    compressMethod,
    powerConsumptionLevel,
    editable,
    colorGroupTable,
    support_literal,
    advanced,
    interactive,
    support_game
  } = ast.attrsMap;
  if (colorGroupTable) {
    WATCHFACE_COLOR_GROUP = colorGroupTable.split(',');
  }
  const isEditable = editable === 'true';
  let themeNum = 0;
  let headerPreviewAddr = 0;
  let dataAddressOffset = 0;
  const recordsList: any[] = [];
  const layoutRecordsList: any = {};
  const rotateImgNameList: string[] = [];
  // 遍历theme标签的属性，给watchface的一些属性赋值，初始化themeData
  Object.entries(ast.children).forEach(([_, curTheme]) => {
    if (curTheme.tag === 'Theme' && curTheme.children.length) {
      const { type, name, bg, preview, colorGroupTable, bgColor, fg } =
        curTheme.attrsMap;
      themeNum++;
      if (type === 'AOD') isAOD = true;
      if (curTheme.attrsMap.isPhotoAlbumWatchface === 'true')
        isPhotoAlbumWatchface = true;
      if (curTheme.attrsMap.isVideoAlbumWatchface === 'true')
        isVideoAlbumWatchface = true;
      const themeName = type.toLowerCase() + '_' + name;
      layoutRecordsList[themeName] = [];
      themeData[themeName] = JSON.parse(JSON.stringify(themePrams.data));
      themeData[themeName].name = name ?? '';
      themeData[themeName].bg = bg ?? '';
      themeData[themeName].fg = fg ?? '';
      if (bgColor) themeData[themeName].bgColor = bgColor;
      themeData[themeName].preview = preview ?? '';
      if (colorGroupTable)
        themeData[themeName].colorGroupTable = colorGroupTable;
      for (const layout of curTheme.children) {
        if (layout.attrsMap._tag && layout.attrsMap._tag === 'Background') {
          continue;
        } else {
          const { x, y, ref, parameter } = layout.attrsMap;
          const layoutRef = ref.replace('@', '');
          const layoutRecordUid = genLayoutId(themeName, LAYOUT);
          const layoutRecord = {
            type: LAYOUT,
            name: layoutRef,
            uid: layoutRecordUid,
            dataAddress: dataAddressOffset,
            dataLength: LAYOUT_DATA_PRE_LENGTH,
            x,
            y,
            parameter
          };
          layoutRecordsList[themeName].push(layoutRecord);
          themeData[themeName].records.push(layoutRecord);
          dataAddressOffset += LAYOUT_DATA_PRE_LENGTH;
        }
      }
    } else {
      // 遍历resources获取旋转图片，N65A对旋转图片ezip采取另一种方式，旋转图片包括图片数字、图片键值对、指针
      const resources = curTheme.children;
      resources.map((res: Ast) => {
        if (
          [
            'DataItemImageValues',
            'DataItemImageNumber',
            'DataItemPointer'
          ].indexOf(res.tag) !== -1
        ) {
          const rotateImgName = res.attrsMap.ref.replace('@', '');
          const rotation = res.attrsMap.rotation;
          if (
            rotateImgNameList.indexOf(rotateImgName) === -1 &&
            rotation !== '0'
          )
            rotateImgNameList.push(rotateImgName);
        } else if (res.tag === 'DataItemArcProgressBar') {
          if (res.attrsMap.indicatorImage) {
            const rotateImgName = res.attrsMap.indicatorImage.replace('@', '');
            rotateImgNameList.push(rotateImgName);
          }
        }
      });
    }
  });

  // 遍历resources下的各个资源, 将各类资源格式化成为record 和 data 两部分
  const resources =
    ast.children.find((val) => val.tag === 'Resources')?.children ?? [];
  const dataList: any[] = [];
  for (const res of resources) {
    let data: any;
    if (res.tag === 'Image') {
      data = await genImageData(
        res,
        resourcePath,
        dataAddressOffset,
        recordsList,
        compressMethod,
        is16bpp,
        isN65Plus,
        isN65Plus ? rotateImgNameList.indexOf(res.attrsMap.name) !== -1 : false,
        isN65C
      );
    } else if (res.tag === 'ImageArray') {
      data = await genImageArrayData(
        res,
        resourcePath,
        dataAddressOffset,
        recordsList,
        compressMethod,
        is16bpp,
        isN65Plus,
        isN65Plus ? rotateImgNameList.indexOf(res.attrsMap.name) !== -1 : false,
        isN65C
      );
    } else if (res.tag === 'SpriteGroup') {
      data = genMultiSpriteData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'Sprite') {
      data = genSpriteData(res, dataAddressOffset, recordsList, dataList);
    } else if (res.tag === 'DataItemImageNumber') {
      data = genDataItemImageNumberData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'DataItemImageValues') {
      data = genDataItemImageValuesData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'Widget') {
      data = genWidgetData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'Slot') {
      data = genSlotData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'Translation') {
      data = {
        ...genTranslationData(res, dataAddressOffset),
        children: res.children
      };
      if (!Multilingual) Multilingual = true;
    } else if (res.tag === 'DataItemPointer') {
      data = genDataItemPointerData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'DataItemArcProgressBar') {
      data = genDataItemArcProgressBarData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'DataItemLineProgressBar') {
      data = genDataItemLineProgressBarData(
        res,
        dataAddressOffset,
        recordsList
      );
    } else if (res.tag === 'DataItemText') {
      data = genDataItemTextData(res, dataAddressOffset, recordsList);
    } else if (res.tag === 'File' && res.attrsMap.fileName?.endsWith('.png')) {
      if (
        res.attrsMap.fileName.startsWith('_riveExt') ||
        res.attrsMap.fileName.startsWith('_spriteExt')
      )
        continue;
      data = await genJSImageData(
        res,
        resourcePath,
        dataAddressOffset,
        compressMethod
      );
    } else if (res.tag === 'File') {
      if (
        res.attrsMap.fileName.startsWith('_riveExt') ||
        res.attrsMap.fileName.startsWith('_spriteExt')
      )
        continue;
      data = await genJSData(
        res,
        resourcePath,
        dataAddressOffset,
        compressMethod
      );
    } else if (res.tag === 'Condition') {
      data = await getConditionData(res, dataAddressOffset);
    } else if (res.tag === 'Animation') {
      data = await getAnimationData(res, dataAddressOffset);
    }
    if (data) {
      const originRecord = data.originRecord;
      const originData = data.originData;
      if (name === '@watchfaceName' && res.attrsMap.name === 'watchfaceName') {
        languages.set('language', originRecord);
      }
      console.log(originRecord.type, originRecord.name, originData.totalLength);
      Object.keys(originRecord).length && recordsList.push(originRecord);
      Object.keys(originData).length && dataList.push(originData);
      dataAddressOffset += originData.totalLength || 0;
    }
  }

  // 遍历themeData，将布局Layout数据放入layoutDataList
  const layoutDataList: any[] = [];
  Object.entries(themeData).forEach(([themeName, value], index) => {
    if (value.records.length) {
      for (const layoutRecord of layoutRecordsList[themeName]) {
        const { name, x, y } = layoutRecord;
        const relateRecord = recordsList.find((record) => {
          return (
            record.name === name.replace('@', '') && record.type !== LAYOUT
          );
        });
        if (relateRecord) {
          const { type: relateType, name: relateName } = relateRecord;
          const relateResUid = getRelateRecordUid(
            relateName,
            recordsList,
            relateType
          );
          const layoutData: any = {
            type: LAYOUT,
            resUid: relateResUid,
            x,
            y,
            defaultIndex: 0,
            offset: layoutRecord.dataAddress
          };
          // 从 parameter (ref name) 反查 Slot children 中的 index
          if (layoutRecord.parameter) {
            const slotRes = resources.find(
              (res: Ast) => res.attrsMap.name === layoutRecord.name
            );
            if (slotRes && slotRes.children) {
              const idx = slotRes.children.findIndex(
                (child: Ast) => child.attrsMap.ref === layoutRecord.parameter
              );
              if (idx >= 0) layoutData.defaultIndex = idx;
            }
          }
          layoutDataList.push(layoutData);
          getRelateRecord(
            themeName,
            relateName,
            resources,
            recordsList,
            relateType
          );
        }
      }
    } else return;
    if (value.bg && value.bg !== '') {
      const backGroundImgName = value.bg.replace('@', '');
      getRelateRecord(
        themeName,
        backGroundImgName,
        resources,
        recordsList,
        IMG
      );
    }
    if (value.fg && value.fg !== '') {
      const fgImgName = value.fg.replace('@', '');
      const fgImg: any = recordsList.find(
        (record) => record.name === fgImgName
      );
      getRelateRecord(themeName, fgImgName, resources, recordsList, IMG);
      themeData[themeName].fgUid = fgImg.uid;
    }
    if (value.preview && value.preview !== '') {
      const previewImgName = value.preview.replace('@', '');
      const previewImg: any = recordsList.find(
        (record) => record.name === previewImgName
      );
      if (previewImg) {
        value.previewAddr = previewImg.dataAddress;
        if (index === 0 && value.previewAddr)
          headerPreviewAddr = value.previewAddr;
      }
    }
    value.records = sortByUid(value.records);
  });

  dataList.unshift(...layoutDataList);

  const { headerBin, headerLength } =
    deviceType === 'M62'
      ? genM62HeaderBinary(
          themeNum,
          basicInfo,
          isPhotoAlbumWatchface,
          isEditable
        )
      : genHeaderBinary(
          themeNum,
          basicInfo,
          HEADER_LENGTH,
          Multilingual,
          isEditable,
          isAOD,
          isPhotoAlbumWatchface,
          WATCHFACE_COLOR_GROUP,
          powerConsumptionLevel,
          support_literal,
          advanced,
          interactive,
          isVideoAlbumWatchface,
          protocol,
          support_game
        );
  const themeAndRecordBin =
    deviceType === 'M62'
      ? await genM62ThemeAndRecordBinary(
          themeData,
          themeNum,
          headerLength,
          resourcePath
        )
      : await genThemeAndRecordBinary(
          themeData,
          themeNum,
          headerLength,
          resourcePath,
          deviceType,
          WATCHFACE_COLOR_GROUP
        );
  const dataBinParts: Buffer[] =
    deviceType === 'M62' ? [genM62DataBinary(dataList)] : genDataBinary(dataList);

  // 设定preview图片地址
  const prevOffset = headerLength + themeAndRecordBin.length;

  // header 里 preview_img_data_addr 前共有32个字节
  headerBin.writeInt32LE(headerPreviewAddr + prevOffset, 32);

  // 多语言下设定 header 中的 language ，由于prevOffset的地址不好确定，所以要最后设置，应该也可以在header中直接设置
  if (Multilingual) {
    headerBin.writeInt32LE(
      languages.get('language')?.dataAddress + prevOffset,
      116
    );
    headerBin.writeInt32LE(languages.get('language')?.dataLength, 120);
  }

  // 返回分块数据，避免再次 concat 产生内存峰值
  const totalDataParts: Buffer[] = [headerBin, themeAndRecordBin, ...dataBinParts];

  clearAll();
  WATCHFACE_COLOR_GROUP = [];
  return totalDataParts;
}

function genLayoutId(themeName: string, recordType: number) {
  const uid = uint32(
    (recordType << 24) +
      (0 << 23) +
      themeData[themeName].recordsCount[recordType]
  );
  themeData[themeName].recordsCount[recordType]++;
  return uid;
}

function getRelateRecord(
  themeName: string,
  relateRef: string,
  resources: Ast[],
  recordsList: any[],
  type: number
) {
  const relateRes = resources.filter((res) => {
    if (
      res.attrsMap.name ===
      (relateRef.startsWith('@') ? relateRef.slice(1) : relateRef)
    ) {
      const { colorGroup } = res.attrsMap;
      const curRecord = recordsList.find((record) => {
        return (
          record.name === res.attrsMap.name &&
          (record.colorGroup === colorGroup || !colorGroup)
        );
      });
      if (curRecord) {
        const isExistInRecords = themeData[themeName].records.find(
          (existRecord) => {
            return curRecord === existRecord;
          }
        );
        if (!isExistInRecords) {
          themeData[themeName].records.push(curRecord);
          return true;
        } else return false;
      } else return false;
    } else return false;
  });
  if (!relateRes.length) return;
  themeData[themeName].recordsCount[type] += relateRes.length;
  if (type === FILE) {
    const fileName = relateRes[0].attrsMap.fileName ?? '';
    if (
      fileName.endsWith('.lua') ||
      fileName.endsWith('.js') ||
      fileName.endsWith('.riv')
    ) {
      resources.map((res) => {
        if (res.tag === 'File') {
          const { name, fileName } = res.attrsMap;
          if (
            fileName.endsWith('.rle') ||
            fileName.endsWith('.bin') ||
            fileName.endsWith('.png')
          ) {
            getRelateRecord(themeName, name, resources, recordsList, FILE);
          }
        }
      });
    }
  }
  if (type === WIDGET || type === SLOT) {
    const children = relateRes[0].children;
    const { preview, bg, icon, editBox, jumpApp, args, condition } =
      relateRes[0].attrsMap;
    if (preview)
      getRelateRecord(themeName, preview, resources, recordsList, IMG);
    if (bg) getRelateRecord(themeName, bg, resources, recordsList, IMG);
    if (icon) getRelateRecord(themeName, icon, resources, recordsList, IMG);
    if (editBox)
      getRelateRecord(themeName, editBox, resources, recordsList, IMG);
    if ((jumpApp === 'jsApplication' || jumpApp === 'luaApplication') && args) {
      resources.map((res) => {
        if (res.tag === 'File') {
          const { name, fileName } = res.attrsMap;
          if (fileName.endsWith('.js') || fileName.endsWith('.lua')) {
            getRelateRecord(themeName, args, resources, recordsList, FILE);
          } else {
            getRelateRecord(themeName, name, resources, recordsList, FILE);
          }
        }
      });
    }
    if (Multilingual && type === WIDGET) {
      const { widgetName, groupType } = relateRes[0].attrsMap;
      if (widgetName)
        getRelateRecord(
          themeName,
          widgetName,
          resources,
          recordsList,
          TRANSLATION
        );
      if (groupType)
        getRelateRecord(
          themeName,
          groupType,
          resources,
          recordsList,
          TRANSLATION
        );
    }
    if (condition) {
      getRelateRecord(themeName, condition, resources, recordsList, CONDITION);
    }
    if (children.length) {
      children.map((child: Ast) => {
        // Slot组件下，不处理Position组件
        if (child.tag === 'Position') return;
        const { ref: childRef, condition, animation } = child.attrsMap;
        [condition, animation, ...childRef.split(' ')]
          .filter((item) => !!item)
          .forEach((item) => {
            const childRecord = recordsList.find((record) => {
              return (
                record.name === (item.startsWith('@') ? item.slice(1) : item)
              );
            });
            if (!item || !childRecord) return;

            const childType = childRecord.type;
            getRelateRecord(themeName, item, resources, recordsList, childType);
          });
      });
    } else return;
  } else if (type !== IMG && type !== IMG_ARRAY && type !== TRANSLATION) {
    const { ref, bg, indicatorImage, unitIcon, literal_crop_font, name } =
      relateRes[0].attrsMap;
    if (name.startsWith('SpriteGroup')) {
      const spriteGroup = resources.find(
        (item) => item.tag === 'SpriteGroup' && item.attrsMap.name == name
      );
      if (!spriteGroup) return;
      spriteGroup.children.map((child: Ast) => {
        const { ref: childRef, refExt } = child.attrsMap;
        const childType = refExt
          ? IMG
          : childRef.endsWith('mp4')
          ? FILE
          : IMG_ARRAY;
        if (childRef) {
          getRelateRecord(
            themeName,
            childRef,
            resources,
            recordsList,
            childType
          );
        }
      });
      return;
    }
    if (ref) {
      const relateType = recordsList.find((record) => {
        return record.name === (ref.startsWith('@') ? ref.slice(1) : ref);
      }).type;
      getRelateRecord(themeName, ref, resources, recordsList, relateType);
    }
    if (bg) getRelateRecord(themeName, bg, resources, recordsList, IMG);
    if (indicatorImage)
      getRelateRecord(themeName, indicatorImage, resources, recordsList, IMG);
    if (unitIcon)
      getRelateRecord(themeName, unitIcon, resources, recordsList, IMG);
    if (literal_crop_font)
      getRelateRecord(
        themeName,
        literal_crop_font,
        resources,
        recordsList,
        FILE
      );
  } else return;
}

function sortByUid(records: any) {
  const res = records.sort((a: any, b: any) => {
    const uid_a = a.uid[0] + (a.uid[1] << 8) + (a.uid[2] << 16);
    const uid_b = b.uid[0] + (b.uid[1] << 8) + (b.uid[2] << 16);
    return a.uid[3] < b.uid[3] || (a.uid[3] === b.uid[3] && uid_a < uid_b)
      ? -1
      : 1;
  });
  return res;
}

export default xmlConvert;
